<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    elem-id="modal_crud_so-additional-service-prices"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="isDeleteMode ? (isDeleting ? t('Desligando') : t('Desligar')) : (isSaving ? t('Salvando') : t('Salvar'))"
    :save-fn="isDeleteMode ? handleDelete : handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <!-- Desligar (soft-delete com semântica: a franquia desliga o adicional) -->
      <div v-if="isDeleteMode" class="delete-confirmation" data-testid="so-additional-service-prices-delete-confirm">
        <p>{{ t('Desligar este adicional para a franquia?') }}</p>
        <p class="text-muted">{{ entryToDelete?.so_additional_service_name }} — {{ entryToDelete?.franquia }}</p>
        <p class="text-muted">{{ t('Ele some do PDV da franquia e a linha continua aqui para religar.') }}</p>
      </div>

      <div v-else>
        <s-o-additional-service-prices-form
          ref="formRef"
          :entryId="currentEntryId"
          :isEditMode="isEditMode"
          @submit-success="handleFormSuccess"
          @submit-error="handleFormError"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOAdditionalServicePrices } from '../../composables/useSOAdditionalServicePrices';
import { eventBus } from '../../eventBus';
import SOAdditionalServicePricesForm from './SOAdditionalServicePricesForm.vue';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface EntryData {
  so_additional_service_price_id?: Id;
  so_additional_service_name?: string;
  franquia?: string;
  [key: string]: unknown;
}

interface FormInstance {
  resetForm: () => void;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
}

interface ModalOpenPayload {
  id?: Id;
  data?: EntryData | null;
  action?: 'create' | 'edit' | 'delete';
}

const { t } = useI18n();

const { remove } = useSOAdditionalServicePrices();

const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const isDeleteMode = ref<boolean>(false);
const currentEntryId = ref<Id | null>(null);
const entryToDelete = ref<EntryData | null>(null);
const formRef = ref<FormInstance | null>(null);
const isSaving = ref<boolean>(false);
const isDeleting = ref<boolean>(false);

const modalTitle = computed(() => {
  if (isDeleteMode.value) {
    return t('Desligar adicional na franquia');
  } else if (isEditMode.value) {
    return t('Editar preço do adicional');
  }
  return t('Adicionar preço de adicional');
});

const openForCreate = (): void => {
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentEntryId.value = null;

  nextTick(() => {
    if (formRef.value) {
      formRef.value.resetForm();
    }
  });
};

const openForEdit = async (entryId: Id): Promise<void> => {
  isVisible.value = true;
  isEditMode.value = true;
  isDeleteMode.value = false;
  currentEntryId.value = entryId;

  await nextTick();

  if (formRef.value) {
    await formRef.value.loadForEdit();
  }
};

const openForDelete = (entryId: Id, entryData: EntryData | null = null): void => {
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = true;
  currentEntryId.value = entryId;
  entryToDelete.value = entryData;
};

const handleClose = (): void => {
  isVisible.value = false;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentEntryId.value = null;
  entryToDelete.value = null;
  isSaving.value = false;
  isDeleting.value = false;

  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  if (formRef.value) {
    isSaving.value = true;
    try {
      await formRef.value.handleSubmit();
    } finally {
      isSaving.value = false;
    }
  }
};

const handleDelete = async (): Promise<void> => {
  if (!currentEntryId.value) {
    logWarn('SOAdditionalServicePricesModal', 'handleDelete', 'No entry ID');
    return;
  }

  isDeleting.value = true;

  try {
    await remove({ so_additional_service_price_id: Number(currentEntryId.value) });
    eventBus.emit('globalMessage.success', t('Adicional desligado para a franquia — religue quando quiser'));
    eventBus.emit('so-additional-service-prices.deleted');
    handleClose();
  } catch (error: unknown) {
    logError('SOAdditionalServicePricesModal', 'handleDelete', 'Error', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao desligar o adicional') + ': ' + message);
  } finally {
    isDeleting.value = false;
  }
};

const handleFormSuccess = (): void => {
  eventBus.emit('so-additional-service-prices.saved');
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('SOAdditionalServicePricesModal', 'handleFormError', 'Form submission error', error);
};

const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('SOAdditionalServicePricesModal', 'handleModalOpen', 'Open event', data);

  if (data?.action === 'delete' && data?.id) {
    openForDelete(data.id, data.data || null);
  } else if (data?.id) {
    await openForEdit(data.id);
  } else {
    openForCreate();
  }
};

onMounted(() => {
  eventBus.on<ModalOpenPayload>('modal.so-additional-service-prices.open', handleModalOpen);
  eventBus.on('modal.so-additional-service-prices.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off<ModalOpenPayload>('modal.so-additional-service-prices.open', handleModalOpen);
  eventBus.off('modal.so-additional-service-prices.close', handleClose);
});

defineExpose({
  openForCreate,
  openForEdit,
  openForDelete
});
</script>

<style scoped>
.delete-confirmation {
  padding: 20px;
  text-align: center;
}

.delete-confirmation p {
  margin-bottom: 10px;
  font-size: 16px;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}
</style>
